<template>
  <smart-filters
    v-model="smartFiltersModel"
    :schema="filterSchema"
    :default-filters="defaultFilters"
    storage-key="proposals"
    @filter="filtrar"
    @clear="LimpiarFiltros"
  />
</template>

<script setup>
import {
  computed,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import SmartFilters from '@/shared/components/smart_filters.vue';
import { useProposalsStore } from '../proposals_store.js';
import { useAuthStore } from '../../auth/auth_store';
import { useEjecutivoStore } from '@/stores/ejecutivo_store.js';
import { useEstadoSolicitudStore } from '@/stores/estado-solicitud.js';
import { useUsabilidadStore } from '@/stores/usabilidad/usabilidad_store.js';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';

import { Role } from '@/enum/roles_enum.js';
import { initial_proposals_filters } from '../proposals_model.js';

const proposalsStore = useProposalsStore();
const authStore = useAuthStore();
const ejecutivoStore = useEjecutivoStore();
const estadoSolicitudStore = useEstadoSolicitudStore();
const usabilidadStore = useUsabilidadStore();
const prospectStore = useProspectStore();
const { estadoSolicitudList } = storeToRefs(estadoSolicitudStore);
const { filters } = storeToRefs(proposalsStore);
const { findAllProposalsHTTP, setPage, emptyProposals, resetFilters } = proposalsStore;
const { searchProspectsHTTP } = prospectStore;
const usuario = authStore.usuario();
const isAdmin = computed(() => usuario?.roles?.some((r) => r.Codigo === Role.ADMINISTRADOR));
const ejecutivoDisabled = computed(() => !isAdmin.value);
const estadosValidosET = computed(() => {
  if (!estadoSolicitudList.value) return [];
  const codigosPermitidos = [1, 3, 5];
  return estadoSolicitudList.value.filter((estado) => codigosPermitidos.includes(estado.Codigo));
});

const defaultFilters = JSON.parse(JSON.stringify(initial_proposals_filters));

const formatearFechaParaUI = (val) => {
  if (!val) return null;
  const d = new Date(val);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const filterSchema = computed(() => [
  {
    key: 'FechaCreacionRange',
    label: 'Fecha Propuesta',
    type: 'date_range',
    keyDesde: 'FechaDesde',
    keyHasta: 'FechaHasta',
    fixed: true,
    span: 6,
  },
  {
    key: 'CodigoVendedor',
    label: 'Ejecutivo',
    type: 'select',
    placeholder: 'Seleccionar un ejecutivo',
    fetchOptions: async () => {
      const results = await ejecutivoStore.searchEjecutivosHTTP('');
      return (results || []).map((u) => ({
        ...u,
        NombreCompleto: `${u.Nombre || ''} ${u.Apellido || ''}`.trim(),
      }));
    },
    fieldKey: 'Codigo',
    fieldLabel: 'NombreCompleto',
    span: 4,
    fixed: !isAdmin.value,
    disabled: ejecutivoDisabled.value,
  },
  {
    key: 'Estado',
    label: 'Estado',
    type: 'select',
    placeholder: 'Seleccionar estado',
    options: estadosValidosET.value,
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 4,
  },
  {
    key: 'Cliente',
    label: 'Cliente',
    type: 'autocomplete',
    placeholder: 'Ingresar cliente',
    fieldLabel: 'Descripcion',
    searchFn: async (query) => {
      if (query && query.length >= 3 && query !== 'null') {
        const results = await searchProspectsHTTP(query);

        return results.map((item) => ({
          ...item,
          value: item.Descripcion,
        }));
      }
      return [];
    },
    span: 4,
  },
  {
    key: 'CodigoCotizacion',
    label: 'Cód. Cotización',
    type: 'input',
    placeholder: 'Ej: 5021, 5022',
    span: 3,
  },
  {
    key: 'CodigoEspecificacionTecnica',
    label: 'Cód. ET',
    type: 'input',
    placeholder: 'Ej: 3401, 3402',
    span: 3,
  },
  {
    key: 'CodigoEquivalente',
    label: 'Cód. Equivalente',
    type: 'input',
    placeholder: 'Ej: 123-A',
    span: 4,
  },
  {
    key: 'CodigoAdicional',
    label: 'Cód. Adicional',
    type: 'input',
    placeholder: 'Ej: SK-900',
    span: 4,
  },
  {
    key: 'Referencia',
    label: 'Producto',
    type: 'input',
    placeholder: 'Ingresar producto',
    span: 4,
  },
  {
    key: 'DescripcionComercial',
    label: 'Descripción Material',
    type: 'input',
    placeholder: 'Ej: térmico, bpp',
    span: 4,
  },
  {
    key: 'CodigoUsabilidad',
    label: 'Usabilidad',
    type: 'select',
    placeholder: 'Seleccionar usabilidad',
    fetchOptions: async () => {
      await usabilidadStore.findAllUsabilidadHTTP({ page: 0 });
      return usabilidadStore.usabilidadLista;
    },
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 4,
  },
  {
    key: 'CodigoPropuesta',
    label: 'Nro. Propuesta',
    type: 'input',
    placeholder: 'Ej: 100, 101',
    span: 3,
  },
]);

const smartFiltersModel = computed({
  get: () => {
    const sf = {};
    filterSchema.value.forEach((f) => {
      if (f.type === 'date_range') {
        const desde = filters.value[f.keyDesde]
          ? formatearFechaParaUI(filters.value[f.keyDesde])
          : null;
        const hasta = filters.value[f.keyHasta]
          ? formatearFechaParaUI(filters.value[f.keyHasta])
          : null;
        if (f.fixed || desde || hasta) {
          sf[f.key] = { field: f, value: [desde, hasta] };
        }
      } else if (f.key === 'CodigoVendedor') {
        if (filters.value.CodigoVendedor !== null && filters.value.CodigoVendedor !== undefined) {
          const nombreAExhibir = !isAdmin.value
            ? `${usuario.Nombre} ${usuario.Apellido}`.trim()
            : '';
          sf[f.key] = {
            field: f,
            value: { Codigo: filters.value.CodigoVendedor, NombreCompleto: nombreAExhibir },
          };
        }
      } else if (f.key === 'Cliente') {
        if (filters.value.Cliente) {
          sf[f.key] = { field: f, value: { Descripcion: filters.value.Cliente } };
        }
      } else if (f.key === 'Estado') {
        const val = filters.value.Estado;
        if (val && val.Codigo !== null && val.Codigo !== undefined) {
          sf[f.key] = { field: f, value: val };
        }
      } else if (f.key === 'CodigoUsabilidad') {
        if (
          filters.value.CodigoUsabilidad !== null &&
          filters.value.CodigoUsabilidad !== undefined
        ) {
          sf[f.key] = { field: f, value: { Codigo: filters.value.CodigoUsabilidad } };
        }
      } else {
        if (
          filters.value[f.key] !== null &&
          filters.value[f.key] !== undefined &&
          filters.value[f.key] !== ''
        ) {
          sf[f.key] = { field: f, value: filters.value[f.key] };
        }
      }
    });
    return sf;
  },
  set: (newSf) => {
    const baseEstructura = {
      FechaDesde: null,
      FechaHasta: null,
      Estado: null,
      CodigoVendedor: null,
      Cliente: null,
      Referencia: null,
      CodigoCotizacion: null,
      CodigoEquivalente: null,
      CodigoAdicional: null,
      DescripcionComercial: null,
      CodigoEspecificacionTecnica: null,
      CodigoUsabilidad: null,
      CodigoPropuesta: null,
    };

    Object.values(newSf).forEach((item) => {
      if (!item || !item.field) return;

      if (item.field.type === 'date_range') {
        baseEstructura[item.field.keyDesde] = item.value?.[0]
          ? new Date(`${item.value[0]}T00:00:00`)
          : null;
        baseEstructura[item.field.keyHasta] = item.value?.[1]
          ? new Date(`${item.value[1]}T23:59:59`)
          : null;
      } else if (item.field.key === 'CodigoVendedor' || item.field.key === 'CodigoUsabilidad') {
        const val = item.value;
        baseEstructura[item.field.key] =
          typeof val === 'object' && val !== null ? val.Codigo : (val ?? null);
      } else if (item.field.key === 'Cliente') {
        const val = item.value;
        baseEstructura.Cliente =
          typeof val === 'object' && val !== null ? val.Descripcion : (val ?? null);
      } else if (item.field.key === 'Estado') {
        baseEstructura.Estado = item.value;
      } else {
        baseEstructura[item.field.key] = item.value;
      }
    });

    if (!isAdmin.value) {
      baseEstructura.CodigoVendedor = usuario.Codigo;
    }

    for (const key in baseEstructura) {
      proposalsStore.filters[key] = baseEstructura[key];
    }
  },
});

const filtrar = () => {
  try {
    emptyProposals();
    setPage(1);
    findAllProposalsHTTP();
  } catch (error) {
    console.error('Error al filtrar propuestas:', error);
  }
};

const LimpiarFiltros = () => {
  setPage(1);
  emptyProposals();
  resetFilters();
  if (!isAdmin.value) {
    proposalsStore.filters.CodigoVendedor = usuario.Codigo;
  }
  filtrar();
};

onBeforeMount(async () => {
  await estadoSolicitudStore.firstEstadoSolicitud();
  if (!isAdmin.value && !filters.value.CodigoVendedor) {
    proposalsStore.filters.CodigoVendedor = usuario.Codigo;
  }
});
</script>
